import { useEffect } from 'react';

/**
 * Safety net for a Radix bug: the `pointer-events: none` lock Radix puts on `document.body` can stick
 * when a close coincides with a synchronous route change, freezing ALL mouse input until a reload.
 */
export function useReleaseBodyPointerEventsOnUnmount(): void {
  useEffect(() => {
    return () => {
      if (typeof document === 'undefined') return;
      // Defer past React's removal of THIS content's DOM node: a synchronous check still matches the
      // unmounting element's `role="dialog"`, so every close would look like a still-open modal.
      queueMicrotask(() => {
        // `:not([data-non-modal])` skips surfaces that never lock the page — the assistant
        // drawer is a role="dialog" that can stay open for the whole session.
        const anotherModalOpen = document.querySelector(
          '[role="dialog"]:not([data-non-modal]),[role="alertdialog"]:not([data-non-modal])',
        );
        if (!anotherModalOpen && document.body.style.pointerEvents === 'none') {
          document.body.style.pointerEvents = '';
        }
      });
    };
  }, []);
}

/**
 * Render this as a child of a Radix `DialogContent` / `AlertDialogContent` so it shares the Content's
 * presence lifecycle and releases a stuck body pointer-events lock when the Content unmounts.
 */
export function ReleaseBodyPointerEventsGuard(): null {
  useReleaseBodyPointerEventsOnUnmount();
  return null;
}
